import type { ProjectStatus, OcrStatus } from './pocketbase'

/**
 * Convert satang (1/100 THB) to formatted THB currency string
 */
export function formatTHB(satang: number | null | undefined): string {
  if (satang == null) return '-'
  const thb = satang / 100
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(thb)
}

/**
 * Format a number with Thai locale
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '-'
  return new Intl.NumberFormat('th-TH').format(value)
}

/**
 * Convert Buddhist Era year to Christian Era (for display reference)
 */
export function beToAd(beYear: string | number): number {
  return Number(beYear) - 543
}

/**
 * Format date for display
 */
export function formatDate(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!dateString) return '-'

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'medium',
    ...options,
  }).format(date)
}

/**
 * Format date and time for display
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-'

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('th-TH', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

/**
 * Format relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return '-'

  const date = new Date(dateString)
  if (isNaN(date.getTime())) return '-'

  const rtf = new Intl.RelativeTimeFormat('th', { numeric: 'auto' })
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

  if (Math.abs(diffDays) < 1) {
    const diffHours = Math.round(diffMs / (1000 * 60 * 60))
    if (Math.abs(diffHours) < 1) {
      const diffMinutes = Math.round(diffMs / (1000 * 60))
      return rtf.format(diffMinutes, 'minute')
    }
    return rtf.format(diffHours, 'hour')
  }

  if (Math.abs(diffDays) < 30) {
    return rtf.format(diffDays, 'day')
  }

  const diffMonths = Math.round(diffDays / 30)
  if (Math.abs(diffMonths) < 12) {
    return rtf.format(diffMonths, 'month')
  }

  const diffYears = Math.round(diffDays / 365)
  return rtf.format(diffYears, 'year')
}

/**
 * Format file size in human readable format
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '-'

  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unitIndex = 0

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

/**
 * Get CSS class for project status badge (eGP schema keys)
 */
export function getStatusClass(status: ProjectStatus | null | undefined): string {
  if (!status) return 'bg-slate-100 text-slate-600'

  const classes: Record<ProjectStatus, string> = {
    'tor-prep': 'bg-slate-100 text-slate-700',
    'purchase-request': 'bg-amber-100 text-amber-700',
    'invitation-issued': 'bg-blue-100 text-blue-700',
    'award-approved': 'bg-emerald-100 text-emerald-700',
    'contract-management': 'bg-violet-100 text-violet-700',
    'project-cancelled': 'bg-rose-100 text-rose-700',
  }

  return classes[status] || 'bg-slate-100 text-slate-600'
}

/**
 * Get CSS class for OCR status badge
 */
export function getOcrStatusClass(status: OcrStatus | null | undefined): string {
  if (!status) return 'bg-slate-100 text-slate-600'

  const classes: Record<OcrStatus, string> = {
    pending: 'bg-slate-100 text-slate-600',
    processing: 'bg-amber-100 text-amber-700',
    done: 'bg-emerald-100 text-emerald-700',
    failed: 'bg-rose-100 text-rose-700',
  }

  return classes[status] || 'bg-slate-100 text-slate-600'
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string | null | undefined, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Generate initials from a name
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?'

  const words = name.trim().split(/\s+/)
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase()
  }

  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
}

/**
 * Classnames utility (simple cn helper with object support)
 */
type ClassValue = string | undefined | null | false | Record<string, boolean>

export function cn(...classes: ClassValue[]): string {
  return classes
    .flatMap((cls) => {
      if (!cls) return []
      if (typeof cls === 'string') return [cls]
      // Handle object syntax: { 'class-name': condition }
      return Object.entries(cls)
        .filter(([, condition]) => condition)
        .map(([className]) => className)
    })
    .join(' ')
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}
